import { FunctionComponent } from 'react'
import {
  Box,
  Button,
  IconButton,
  rucksack,
  Typography,
} from '@black-bear-energy/rucksack'
import {
  CheckRounded,
  CheckCircleRounded,
  CloseRounded,
  ErrorOutlineRounded,
} from '@mui/icons-material'
import { CenteredModal } from '../CenteredModal'
import { BoldText } from './BoldText'
import { formatTableDate } from '../helpers/formatTableDate'
import { ChecklistDocumentReview } from '@black-bear-energy/black-bear-energy-common'

export const CommentModal: FunctionComponent<CommentModalProps> = ({
  open,
  documentName,
  review,
  pendingReview,
  handleClose,
}) => {
  function formatDateTime(dateTime: Date): string {
    /** A string of the date in this format: Aug 5, 2025 */
    const dateString = formatTableDate(dateTime)

    /** A string of the time in this format: 13:51 */
    const timeString = dateTime
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      })
      .replace(/^0/, '')

    return `${dateString} ${timeString}`
  }

  const statusText = pendingReview
    ? 'Pending Review'
    : review.verified
    ? 'Verified'
    : 'Rejected'

  const statusIcon = pendingReview ? (
    <ErrorOutlineRounded sx={{ color: rucksack.palette.warning.main }} />
  ) : review.verified ? (
    <CheckCircleRounded sx={{ color: rucksack.palette.success.main }} />
  ) : (
    <ErrorOutlineRounded sx={{ color: rucksack.palette.error.main }} />
  )

  return (
    <CenteredModal open={open}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          alignSelf: 'stretch',
        }}
      >
        <Typography variant="h6">{documentName}</Typography>
        <IconButton onClick={handleClose}>
          <CloseRounded />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '16px',
          alignSelf: 'stretch',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Typography variant="body1">{statusText}</Typography>
          {statusIcon}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            <Typography variant="body1">
              {pendingReview ? 'Previously ' : ''}Reviewed By:
            </Typography>
            <Typography variant="body1">{review.reviewer}</Typography>
          </Box>
          <Typography variant="body1">
            {formatDateTime(review.reviewedAt)}
          </Typography>
        </Box>

        <Typography variant="body1">
          <BoldText>Comment: </BoldText>
          {review.comment}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          alignSelf: 'stretch',
        }}
      >
        <Button
          onClick={handleClose}
          startIcon={<CheckRounded />}
          sx={{
            backgroundColor: rucksack.palette.primary.light,
            borderRadius: rucksack.shape.borderRadius,
            color: rucksack.palette.primary.contrastText,
            fontSize: '14px',
            fontWeight: 500,
            padding: '6px 14px',
            boxShadow:
              '0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.20);',
          }}
        >
          Close
        </Button>
      </Box>
    </CenteredModal>
  )
}

export interface CommentModalProps {
  open: boolean
  documentName: string
  review: ChecklistDocumentReview
  pendingReview: boolean
  handleClose: () => void
}
